/*******************************************************************************
  UpcomingSessions.js

  Author: Advait;
*******************************************************************************/
import React, { useState, useEffect } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { getTenantsWithSitesAdmin } from "../apihelpers/graphqlhelpers";
import {
  processUpcomingSessionData,
  generateTimeSlots,
  getSlotStatus,
  matchFacilityStatesWithSchedules,
} from "../components/helper/upcomingSessions/processUpcomingSessionData";
import "../index.css";

const GET_SCHEDULE = `query ListScheduleItems(
  $filter: ModelScheduleItemFilterInput
  $limit: Int
  $nextToken: String
) {
  listScheduleItems(filter: $filter, limit: $limit, nextToken: $nextToken) {
    items { siteId type day startTime duration }
    nextToken
  }
}`;

// Helper function to normalize sites array from tenant
const getSitesFromTenant = (tenant) => tenant.sites?.items || tenant.sites || [];

function ScheduledSummary() {
  const [loading, setLoading] = useState(true);
  const [scheduledRounds, setScheduledRounds] = useState([]);
  const currentHour = new Date().getHours();

  useEffect(() => {
    const loadData = async () => {
      try {
        // Data fetching - admin only access
        const tenants = await getTenantsWithSitesAdmin(false);
        // Exclude disinfection and disinfection_allowed types
        const fetchAllSchedules = async () => {
          let allSchedules = [];
          let nextToken = null;

          do {
            const result = await API.graphql(
              graphqlOperation(GET_SCHEDULE, {
                filter: {
                  and: [
                    { enabled: { eq: true } },
                    { type: { ne: "disinfection" } },
                    { type: { ne: "disinfection_allowed" } },
                  ],
                },
                limit: 1000,
                nextToken,
              })
            );
            allSchedules = allSchedules.concat(result.data.listScheduleItems.items);
            nextToken = result.data.listScheduleItems.nextToken;
          } while (nextToken);

          return allSchedules;
        };

        const allSchedules = await fetchAllSchedules();

        // Create a Set of valid site IDs from tenants (only enabled sites)
        const validSiteIds = new Set(
          tenants.flatMap((tenant) => getSitesFromTenant(tenant).map((site) => site.id))
        );

        // Filter valid schedules and group by siteId
        const validSchedules = allSchedules.filter((schedule) =>
          validSiteIds.has(schedule.siteId)
        );
        const schedulesBySiteId = validSchedules.reduce((acc, schedule) => {
          (acc[schedule.siteId] = acc[schedule.siteId] || []).push(schedule);
          return acc;
        }, {});

        // Fetch ongoing robot sessions for all sites and build schedule data
        const allScheduleData = [];
        const allOngoingSessions = [];

        const promises = tenants.flatMap((tenant) =>
          getSitesFromTenant(tenant).map(async (site) => {
            // Get schedules for this site from the grouped data
            const siteSchedules = schedulesBySiteId[site.id] || [];

            // Fetch ongoing robot session state
            let ongoingSession = null;
            try {
              const response = await API.get("fataskman", `/sites/${site.id}`);
              const state = response?.facilitiesState;
              if (state && ["RECEPTIONIST", "INSPECTION", "SECURITY_PATROL"].includes(state)) {
                ongoingSession = {
                  site,
                  tenant,
                  facilitiesState: state,
                };
              }
            } catch (error) {
              console.log("Error fetching ongoing session:", error);
            }

            allScheduleData.push({
              site,
              tenant,
              schedules: siteSchedules,
            });

            if (ongoingSession) {
              allOngoingSessions.push(ongoingSession);
            }
          })
        );

        await Promise.all(promises);

        // Process the fetched data using helper function
        const processedRounds = processUpcomingSessionData(allScheduleData);

        // Match ongoing sessions with schedules to get timing information
        const matchedOngoingSessions = matchFacilityStatesWithSchedules(
          allOngoingSessions,
          processedRounds
        );
        setScheduledRounds({
          scheduledRounds: processedRounds,
          ongoingSessions: matchedOngoingSessions,
        });
      } catch (error) {
        console.error("Error getting site data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Generate UI data using helper functions
  const timeSlots = generateTimeSlots(currentHour);

  return (
    <div className="fullPageDiv">
      <h3>Sessions Summary</h3>

      {loading ? (
        <div>Loading schedule...</div>
      ) : (
        <div className="schedule-container">
          <table className="schedule-table">
            <thead>
              <tr className="schedule-table-header">
                <th className="schedule-table-header-cell">Time</th>
                <th className="schedule-table-header-cell">Status</th>
              </tr>
            </thead>
            <tbody>
              {timeSlots.map((slot, index) => {
                const status = getSlotStatus(
                  slot.hour,
                  index,
                  currentHour,
                  scheduledRounds.scheduledRounds,
                  scheduledRounds.ongoingSessions
                );
                return (
                  <tr
                    key={index}
                    className={slot.isCurrentHour ? "schedule-current-hour" : ""}
                  >
                    <td className="schedule-table-cell schedule-time-cell">
                      {slot.timeString}
                    </td>
                    <td
                      className={`schedule-table-cell ${status ? "schedule-status-cell" : ""}`}
                      title={status?.tooltip || ""}
                    >
                      {status && <span className="status-indicator">{status.text}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ScheduledSummary;
